import { addDays, endOfDay, startOfDay } from "date-fns";

import {
    BugGroupEntity,
    PhaseEntity,
    ProjectEntity,
    ProjectUserEntity,
    RegionEntity,
} from "@archipad-js/archipad-model";
import { Expression } from "@archipad-js/core/query";
import { LoggerManager } from "@archipad-js/core/logger";

import { PhaseService } from "./phase.service";
import { RegionService } from "./region.service";

/**
 * Dependency-inversion interface providing user/permission data required by {@link VisitFilterService}.
 *
 * Implemented by the legacy `WorkflowUserManager`.
 */
export interface VisitFilterUserProvider {
    listUsers(project: ProjectEntity): readonly ProjectUserEntity[];
    bugGroupsAllowingReadNotNull(project: ProjectEntity): ReadonlyArray<BugGroupEntity>;
}

export class VisitFilterService {
    private readonly logger = LoggerManager.getLogger("VisitFilterService");

    constructor(
        private readonly phaseService: PhaseService,
        private readonly regionService: RegionService,
        private readonly workflowUserManager: VisitFilterUserProvider,
    ) {}

    public listDatePeriods(): readonly DatePeriod[] {
        return [DatePeriod.Future, DatePeriod.Today, DatePeriod.Week, DatePeriod.Month];
    }

    public listPhases({ project }: VisitFilterContext): readonly PhaseEntity[] {
        return this.phaseService.listPhases(project);
    }

    public listRegions({ project }: VisitFilterContext): ReturnType<RegionService["listRegions"]> {
        return this.regionService.listRegions({ project });
    }

    public listUsers({ project }: VisitFilterContext): readonly ProjectUserEntity[] {
        //Filtering out users that have a templateId aka fake user (ex:Archibot)
        return this.workflowUserManager.listUsers(project).filter((user) => !user.templateId);
    }

    /**
     * Returns an {@link Expression} aim to filter a list of {@link VisitEntity}.
     *
     * Will filter all meetings that match every criteria.
     *
     * @param filters Object containing values used to create an {@link Expression} to filter meetings.
     * @returns
     */
    public makeFilterExpression(context: VisitFilterContext, filters: VisitFilterQuery): Expression {
        const expression = Expression.and(
            Expression.or(Expression.parse("visitType == null"), Expression.parse("visitType.eventPriority >= 0")),
            this.makeBugGroupFilterExpression(context),
            this.makeDatePeriodFilterExpression(filters.datePeriod),
            this.makePhaseFilterExpression(filters.phase),
            this.makeRegionFilterExpression(filters.region),
            this.makeUserFilterExpression(filters.user),
        );

        this.logger.debug(expression.toString("pretty"));

        return expression;
    }

    private makeBugGroupFilterExpression(context: VisitFilterContext): Expression {
        const bugGroup = this.resolveBugGroup(context);
        if (bugGroup === undefined) {
            return Expression.false;
        }

        return Expression.parse("visitType.bugGroup == {bugGroup}", { bugGroup });
    }

    private makeDatePeriodFilterExpression(datePeriod: VisitFilterQuery["datePeriod"]): Expression {
        const now = new Date();
        const todayStart = startOfDay(now);
        const todayEnd = endOfDay(now);

        switch (datePeriod) {
            case DatePeriod.Future: {
                return Expression.parse("date >= {todayStart}", { todayStart });
            }
            case DatePeriod.Today: {
                return Expression.parse("date >= {todayStart} AND date < {todayEnd}", { todayStart, todayEnd });
            }
            case DatePeriod.Week: {
                const lastWeek = addDays(todayStart, -7);
                return Expression.parse("date >= {lastWeek} AND date < {todayEnd}", { lastWeek, todayEnd });
            }
            case DatePeriod.Month: {
                const lastMonth = addDays(todayStart, -30);
                return Expression.parse("date >= {lastMonth} AND date < {todayEnd}", { lastMonth, todayEnd });
            }
            default: {
                return Expression.true;
            }
        }
    }

    private makePhaseFilterExpression(phase: VisitFilterQuery["phase"]): Expression {
        if (phase === undefined) {
            return Expression.true;
        }

        return Expression.parse("phase == {phase}", { phase });
    }

    private makeRegionFilterExpression(region: VisitFilterQuery["region"]): Expression {
        if (region === undefined) {
            return Expression.true;
        }

        return Expression.parse("region == {region}", { region });
    }

    private makeUserFilterExpression(user: VisitFilterQuery["user"]): Expression {
        if (user === undefined) {
            return Expression.true;
        }

        return Expression.parse("creator == {user}", { user });
    }

    private resolveBugGroup({
        project,
        bugGroup: bugGroupContext,
    }: VisitFilterContext): BugGroupEntity | null | undefined {
        if (bugGroupContext === undefined) {
            return undefined;
        }

        const bugGroupsAllowingRead = this.workflowUserManager.bugGroupsAllowingReadNotNull(project);
        if (!bugGroupsAllowingRead?.length) {
            return undefined;
        }

        return bugGroupsAllowingRead.find((b) => b === bugGroupContext);
    }
}

export interface VisitFilterContext {
    project: ProjectEntity;
    bugGroup: BugGroupEntity | null;
}

export interface VisitFilterQuery {
    datePeriod?: DatePeriod | undefined;
    phase?: PhaseEntity | null | undefined;
    region?: RegionEntity | null | undefined;
    user?: ProjectUserEntity | undefined;
}

export enum DatePeriod {
    Future,
    Today,
    Week,
    Month,
}
